import React, { useState } from "react";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PayMethodDialog from "@/components/PayMethodDialog";
import { toast } from "sonner";
import { Receipt, Gift, CheckCircle } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Unpaid() {
  const [payFor, setPayFor] = useState(null);
  const [waiveFor, setWaiveFor] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");

  const { data, refetch } = useLiveQuery(
    [TAGS.UNPAID, TAGS.SESSIONS],
    async () => (await api.get("/sessions/unpaid")).data,
  );
  const rows = data || [];

  const submitWaive = async () => {
    if (!waiveFor) return;
    try {
      await api.post(`/sessions/${waiveFor.id}/waive`, { reason: waiveReason });
      toast.success(`${waiveFor.plate} misafir muafiyeti uygulandı`);
      setWaiveFor(null); setWaiveReason("");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Muaf edilemedi"); }
  };

  const openReceipt = async (id) => {
    try {
      const token = localStorage.getItem("otopark_token");
      const r = await fetch(`${BACKEND_URL}/api/sessions/${id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      // Use anchor click to avoid popup blockers
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = `makbuz_${id.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success("Makbuz indirildi");
    } catch (e) {
      toast.error("Makbuz açılamadı: " + (e.message || ""));
    }
  };

  const total = rows.reduce((s, r) => s + (r.fee || 0), 0);
  const totalDiscount = rows.reduce((s, r) => s + (r.discount_amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Bekleyen</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Ödenmemiş Oturumlar</h1>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Toplam Bekleyen</div>
          <div className="text-2xl font-bold font-mono text-destructive">{formatTL(total)}</div>
          {totalDiscount > 0 && (
            <div className="text-xs text-amber-600">İndirim: -{formatTL(totalDiscount)}</div>
          )}
        </div>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Fotoğraf</TableHead>
                <TableHead>Plaka</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Çıkış</TableHead>
                <TableHead>Süre</TableHead>
                <TableHead>Ücret</TableHead>
                <TableHead>İndirim</TableHead>
                <TableHead>İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Ödenmemiş oturum yok</TableCell></TableRow>}
              {rows.map((s) => {
                const isSubscriber = !!s.subscription_live_detected || !!s.is_subscriber;
                return (
                <TableRow key={s.id} data-testid={`admin-unpaid-row-${s.plate}`} className={isSubscriber ? "bg-green-50/60" : ""}>
                  <TableCell>
                    <div className="flex gap-1">
                      <UnpaidPhoto src={s.entry_image_url} entryFallback={s.entry_image_url} label="G" />
                      <UnpaidPhoto src={s.exit_image_url} entryFallback={s.entry_image_url} label="Ç" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Plate country={s.country_code}>{s.plate}</Plate>
                      {isSubscriber && (
                        <Badge
                          data-testid={`admin-unpaid-muaf-${s.plate}`}
                          className="rounded-sm bg-green-600 hover:bg-green-600 text-white uppercase tracking-widest text-[9px] font-semibold gap-1 border-transparent w-fit"
                        >
                          <CheckCircle size={10} weight="fill" /> Muaf (Abone)
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="uppercase text-xs tracking-widest">{s.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(s.exit_time)}</TableCell>
                  <TableCell className="font-mono">{formatDuration(s.duration_minutes)}</TableCell>
                  <TableCell className="font-mono font-bold">
                    {s.original_fee && s.original_fee > s.fee && (
                      <span className="line-through text-muted-foreground text-xs mr-1">{formatTL(s.original_fee)}</span>
                    )}
                    <span className={isSubscriber ? "text-green-700 line-through opacity-70" : "text-destructive"}>{formatTL(s.fee)}</span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.discount_amount ? (
                      <div>
                        <div className="text-amber-600 font-semibold">-{formatTL(s.discount_amount)}</div>
                        <div className="text-muted-foreground">{s.discount_reason || s.discount_kind}</div>
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button data-testid={`pay-${s.plate}`} onClick={() => setPayFor(s)} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase text-xs tracking-widest font-semibold" size="sm">
                        Ödendi
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => { setWaiveFor(s); setWaiveReason(""); }}
                              data-testid={`waive-${s.plate}`}
                              className="rounded-sm text-xs uppercase tracking-widest text-amber-700 hover:text-amber-800 border-amber-300"
                              title="Misafir Muaf Et">
                        <Gift size={14} /> Misafir
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openReceipt(s.id)} data-testid={`receipt-${s.plate}`} title="Makbuz"><Receipt size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PayMethodDialog session={payFor} open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)} onDone={refetch} />

      {/* Misafir Muaf Et Dialog */}
      <Dialog open={!!waiveFor} onOpenChange={(o) => !o && setWaiveFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift size={20} weight="fill" className="text-amber-600" />
              Misafir Muafiyeti
            </DialogTitle>
          </DialogHeader>
          {waiveFor && (
            <div className="space-y-3">
              <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 flex items-center justify-between">
                <Plate country={waiveFor.country_code}>{waiveFor.plate}</Plate>
                <div className="text-xs">
                  <div className="text-muted-foreground uppercase tracking-widest">Muaf Edilecek Tutar</div>
                  <div className="text-lg font-mono font-bold text-amber-700 text-right">{formatTL(waiveFor.fee)}</div>
                </div>
              </div>
              <div>
                <Label>Sebep (opsiyonel)</Label>
                <Input value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)}
                       className="rounded-sm" placeholder="Örn: Patron misafiri, VIP ziyaretçi..."
                       data-testid="waive-reason-input" />
              </div>
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Bu oturum &quot;Muaf&quot; olarak kaydedilecek, ücret sıfırlanacak ve Kasa/Tahsilatlar&apos;da
                muafiyet olarak görünecek. İşlem denetim kayıtlarına düşer.
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setWaiveFor(null)}
                        className="rounded-sm flex-1">İptal</Button>
                <Button onClick={submitWaive}
                        data-testid="waive-confirm-btn"
                        className="rounded-sm flex-1 bg-amber-600 text-white hover:bg-amber-700 uppercase tracking-widest font-semibold">
                  Muaf Et
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function UnpaidPhoto({ src, entryFallback, label }) {
  const [failed, setFailed] = React.useState(false);
  const [triedFallback, setTriedFallback] = React.useState(false);
  const fullSrc = src
    ? (src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`)
    : null;
  const fallbackSrc = (!triedFallback && entryFallback && entryFallback !== src)
    ? (entryFallback.startsWith("http") ? entryFallback : `${process.env.REACT_APP_BACKEND_URL}${entryFallback}`)
    : null;
  const currentSrc = failed && fallbackSrc ? fallbackSrc : fullSrc;
  const canShow = !!currentSrc && !(failed && !fallbackSrc);
  return (
    <div className="relative w-10 h-10 rounded-sm overflow-hidden bg-muted border border-border shrink-0"
         title={label === "G" ? "Giriş fotoğrafı" : "Çıkış fotoğrafı"}>
      {canShow ? (
        <img
          src={currentSrc}
          alt={label}
          className="w-full h-full object-cover"
          onError={() => {
            if (!triedFallback && fallbackSrc) {
              setTriedFallback(true);
              setFailed(true);
            } else {
              setFailed(true);
            }
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground uppercase tracking-widest">
          Yok
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[8px] text-center uppercase tracking-widest">
        {label}
      </div>
    </div>
  );
}
